import React from 'react';
import { AutoComplete, Tag, Typography, message } from 'antd';
import { EmailRecipient, ContactInfo } from './types';
import { isValidEmail, normalizePhoneNumber } from './utils';

const { Text } = Typography;

interface EmailRecipientSelectorProps {
  emailList: string[];
  emailContactInfo: ContactInfo[];
  selectedEmailRecipients: EmailRecipient[];
  emailInputValue: string;
  onInputChange: (value: string) => void;
  onRecipientsChange: (recipients: EmailRecipient[]) => void;
  // isEmailNotificationEnabled: (email: string) => boolean;
}

const EmailRecipientSelector: React.FC<EmailRecipientSelectorProps> = ({
  emailList,
  emailContactInfo,
  selectedEmailRecipients,
  emailInputValue,
  onInputChange,
  onRecipientsChange,
  // isEmailNotificationEnabled,
}) => {
  const handleEmailSelect = (value: string) => {
    if (!value) return;
    // Extract displayName and email from formattedValue "displayName: email"
    const displayName = value.includes(':') ? value.split(':')[0].trim() : '';
    const emailAddress = value.includes(':') ? value.split(':').pop()?.trim() : value.trim();
    if (!emailAddress) {
      message.error('Please enter a valid email address');
      return;
    }

    if (!isValidEmail(emailAddress)) {
      message.error('Invalid email format. Please enter a valid email address.');
      return;
    }

    const normalizedEmail = emailAddress.toLowerCase().trim();
    if (selectedEmailRecipients.some((e) => e.email.toLowerCase().trim() === normalizedEmail)) {
      message.warning('This email address is already in the recipient list.');
      return;
    }

    // Check if email notification is enabled for this user (only if not Anonymous)
    // If name is Anonymous (manually typed), skip the check
    const finalName = displayName || 'Anonymous';
    // if (finalName !== 'Anonymous' && !isEmailNotificationEnabled(emailAddress)) {
    //   message.warning(
    //     'This user has disabled receiving email notifications. Cannot add to recipient list.'
    //   );
    //   onInputChange('');
    //   return;
    // }

    onRecipientsChange([
      ...selectedEmailRecipients,
      {
        email: emailAddress,
        name: finalName,
      },
    ]);
    onInputChange('');
  };

  const handleEmailAdd = (value: string) => {
    if (!value) {
      message.error('Please enter an email address');
      return;
    }

    const emailAddress = value.trim();
    if (!emailAddress) {
      message.error('Please enter a valid email address');
      return;
    }

    if (!isValidEmail(emailAddress)) {
      message.error('Invalid email format. Please enter a valid email address.');
      return;
    }

    const normalizedEmail = emailAddress.toLowerCase().trim();
    if (selectedEmailRecipients.some((e) => e.email.toLowerCase().trim() === normalizedEmail)) {
      message.warning('This email address is already in the recipient list.');
      return;
    }

    // When manually typing, use email as name
    onRecipientsChange([
      ...selectedEmailRecipients,
      {
        email: emailAddress,
        name: 'Anonymous',
      },
    ]);
    onInputChange('');
  };

  const handleEmailRemove = (email: string) => {
    const normalizedEmail = email.toLowerCase().trim();
    onRecipientsChange(
      selectedEmailRecipients.filter((e) => e.email.toLowerCase().trim() !== normalizedEmail)
    );
  };

  const handleEmailKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && emailInputValue && emailInputValue.trim()) {
      e.preventDefault();
      handleEmailAdd(emailInputValue);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Text strong style={{ fontSize: '14px', lineHeight: '28px' }}>
          To:
        </Text>
        {selectedEmailRecipients.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {selectedEmailRecipients.map((recipient) => {
              // Skip notification check for Anonymous recipients (manually typed)
              const isAnonymous = recipient.name === 'Anonymous';
              // const emailEnabled = isAnonymous
              //   ? true
              //   : isEmailNotificationEnabled(recipient.email);
              const emailEnabled = true;
              return (
                <Tag
                  key={recipient.email.toLowerCase().trim()}
                  closable
                  onClose={() => handleEmailRemove(recipient.email)}
                  color={emailEnabled ? 'blue' : 'orange'}
                  style={{
                    fontSize: '13px',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    margin: '0',
                    display: 'inline-flex',
                    alignItems: 'center',
                    lineHeight: '20px',
                  }}
                >
                  {recipient.name}
                </Tag>
              );
            })}
          </div>
        )}
      </div>
      <AutoComplete
        value={emailInputValue}
        onChange={(value) => onInputChange(value || '')}
        onSelect={handleEmailSelect}
        onKeyDown={handleEmailKeyPress}
        options={emailList
          .filter((email) => {
            if (!email) return false;
            const emailAddress = email.includes(':')
              ? email.split(':').pop()?.trim()
              : email.trim();
            const searchValue = emailInputValue || '';
            if (!emailAddress || !isValidEmail(emailAddress)) return false;

            const normalizedEmail = emailAddress.toLowerCase().trim();
            const isSelected = selectedEmailRecipients.some(
              (e) => e.email.toLowerCase().trim() === normalizedEmail
            );

            return (
              !isSelected &&
              (searchValue === '' || email.toLowerCase().includes(searchValue.toLowerCase()))
            );
          })
          .map((email) => {
            // Extract display name (part before ":") for label, keep full string for value
            const displayName = email.includes(':') ? email.split(':')[0].trim() : email;
            const emailAddress = email.includes(':')
              ? email.split(':').pop()?.trim()
              : email.trim();
            const normalizedEmail = emailAddress?.toLowerCase().trim() || '';
            const contact = emailContactInfo.find((item) => {
              const itemEmail = item.email || '';
              return itemEmail.toLowerCase().trim() === normalizedEmail;
            });
            const emailEnabled = contact?.emailNotificationEnabled ?? false;

            return {
              value: email,
              label: (
                <div>
                  <div>{displayName}</div>
                  {/* {!emailEnabled && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#ff9800',
                        marginTop: '2px',
                      }}
                    >
                      User has disabled receiving email notifications
                    </div>
                  )} */}
                </div>
              ),
            };
          })}
        placeholder="Select or type email address, press Enter to add"
        style={{ width: '100%' }}
        size="large"
        allowClear
      />
    </div>
  );
};

export default EmailRecipientSelector;

